import { SearchableConversationFilterInput } from 'generated-graphql/types';

const WORD_SEPARATOR = /[^\p{L}\p{N}']+/u;

const containsLetterOrDigit = (word: string) => /[\p{L}\p{N}]/u.test(word);

/**
 * Builds one clause per word of the search text, each requiring that word to
 * match either participant name field. Callers AND the clauses together.
 *
 * Passing the whole search text to a single `match` does not work: OpenSearch
 * ORs the tokens, so "John Smith" matches every John. Splitting the text and
 * ANDing the clauses is what makes both words count, and keeping each word
 * open to either field means the word order does not have to line up with
 * first name / last name.
 *
 * Words are separated on anything that is not a letter, digit or apostrophe,
 * because OpenSearch breaks words on those characters too. Leaving "Mary-Jane"
 * whole would put the hyphen inside a single `match` and bring back the OR we
 * are trying to avoid. Apostrophes are deliberately kept: OpenSearch indexes
 * "O'Brien" as one word, so splitting there would never match.
 */
export const buildParticipantNameSearchClauses = (
  searchText: string
): SearchableConversationFilterInput[] =>
  searchText
    .split(WORD_SEPARATOR)
    .filter(containsLetterOrDigit)
    .map((word) => ({
      or: [
        { participantFirstName: { match: word } },
        { participantFirstName: { matchPhrasePrefix: word } },
        { participantLastName: { match: word } },
        { participantLastName: { matchPhrasePrefix: word } }
      ]
    }));
